/**
 * ContributorPicker — the "Contributor" row of the FilterBar's More
 * Filters panel.
 *
 * Two surfaces (per 2026-05-13 design review, decision T5):
 *
 *   1. Inline dropdown listing the top-20 contributors for the
 *      current project. Multi-select checkboxes; selection state is
 *      lifted to FilterContext.
 *   2. A "More..." link that opens a searchable modal exposing
 *      *every* contributor (up to 50 matches per query).
 *
 * The two surfaces share the same selection set — the modal is a
 * superset view of the dropdown, not a separate filter.
 *
 * Catalogue load: fetched once per project (cheap PHP query), cached
 * in component state. Re-fetched when the project id changes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import config from "./config";
import { useProject } from "./ProjectContext";
import { useFilter } from "./FilterContext";
import "./ContributorPicker.css";

const TOP_LIMIT = 20;
const PANEL_WIDTH = 320;
const PANEL_GAP = 6; // px between trigger bottom and panel top

export default function ContributorPicker() {
  const { project } = useProject();
  const projectId = project?.projectsid;
  const { selectedContributorIds, setSelectedContributorIds } = useFilter();

  const [top, setTop] = useState([]); // [{id, name, count}]
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false); // dropdown open
  const [modalOpen, setModalOpen] = useState(false);
  // Fixed-positioning coordinates for the floating panel. Recomputed
  // whenever the trigger could have moved (open, scroll, resize) so
  // the panel never gets clipped by the FilterBar's overflow.
  const [panelPos, setPanelPos] = useState(null);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Fetch top-20 + total whenever the project changes. Aborts in-flight
  // when projects switch quickly.
  useEffect(() => {
    if (!projectId) {
      setTop([]);
      setTotal(0);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    axios
      .get(config.getApiUrl("listProjectContributors.php"), {
        params: { projectsid: projectId },
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setTop(Array.isArray(res.data?.top) ? res.data.top : []);
        setTotal(Number(res.data?.total) || 0);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setError("Could not load contributors.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [projectId]);

  // Recompute the floating panel's screen position. The panel is
  // position:fixed (so it escapes ALL parent overflow), anchored just
  // below the trigger. We also flip to right-edge alignment when the
  // panel would overflow the viewport on small screens.
  const recomputePanelPos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewW = window.innerWidth || document.documentElement.clientWidth;
    let left = rect.left;
    if (left + PANEL_WIDTH > viewW - 8) {
      // Pull back so the panel stays on-screen.
      left = Math.max(8, viewW - PANEL_WIDTH - 8);
    }
    setPanelPos({ top: rect.bottom + PANEL_GAP, left });
  }, []);

  // Recompute on open + whenever anything that could move the trigger
  // happens (scroll, resize). Use capture phase for scroll so we catch
  // scrolling inside any parent (e.g. .filter-bar__expanded-body).
  useEffect(() => {
    if (!open) return undefined;
    recomputePanelPos();
    const onScrollOrResize = () => recomputePanelPos();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, recomputePanelPos]);

  // Close the dropdown on outside click. We have to check BOTH the
  // trigger area and the (now-detached) panel because the panel is
  // a fixed-positioned sibling outside containerRef's DOM subtree.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      const inTrigger =
        containerRef.current && containerRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selectedSet = useMemo(
    () => new Set(selectedContributorIds.map((n) => Number(n))),
    [selectedContributorIds],
  );

  const toggleId = useCallback(
    (id) => {
      const n = Number(id);
      const next = new Set(selectedSet);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      setSelectedContributorIds(Array.from(next));
    },
    [selectedSet, setSelectedContributorIds],
  );

  const clearSelection = useCallback(() => {
    setSelectedContributorIds([]);
  }, [setSelectedContributorIds]);

  // Build a label for the trigger button.
  //
  // PR #46 (plan §I1): "1 selected → name in pill (char-capped); >1
  // selected → label 'Multiple'". The previous "name, name +N" format
  // was visually noisy in the main filter row and competed with the
  // theme chips for horizontal space. Single name still resolves
  // through `top`; if the lone selection isn't in the top-20 cache we
  // fall back to the generic "1 contributor" wording rather than
  // showing a numeric id.
  const triggerLabel = useMemo(() => {
    const count = selectedContributorIds.length;
    if (count === 0) return "Any contributor";
    if (count > 1) return "Multiple";
    // Exactly one selected.
    const onlyId = Number(selectedContributorIds[0]);
    const hit = top.find((c) => c.id === onlyId);
    return hit?.name || "1 contributor";
  }, [selectedContributorIds, top]);

  return (
    <div className="contributor-picker" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={[
          "contributor-picker__trigger",
          selectedContributorIds.length > 0
            ? "contributor-picker__trigger--active"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading || (!top.length && total === 0 && !error)}
      >
        <span>{triggerLabel}</span>
        <span aria-hidden="true" className="contributor-picker__caret">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && panelPos && (
        <div
          ref={panelRef}
          className="contributor-picker__panel"
          role="listbox"
          aria-label="Contributors"
          style={{
            position: "fixed",
            top: panelPos.top,
            left: panelPos.left,
            width: PANEL_WIDTH,
          }}
        >
          {loading && (
            <div className="contributor-picker__status">Loading…</div>
          )}
          {error && (
            <div className="contributor-picker__status contributor-picker__status--error">
              {error}
            </div>
          )}
          {!loading && !error && top.length === 0 && (
            <div className="contributor-picker__status">
              No contributors yet for this project.
            </div>
          )}
          {!loading && !error && top.length > 0 && (
            <>
              <ul className="contributor-picker__list">
                {top.map((c) => {
                  const checked = selectedSet.has(c.id);
                  return (
                    <li
                      key={c.id}
                      className="contributor-picker__item"
                      role="option"
                      aria-selected={checked}
                    >
                      {/* Item 6 (client 2026-06-18): toggle TEXT, not a
                          checkbox. The whole row is a button; the name goes
                          bold when selected. aria-pressed keeps the toggle
                          semantics for keyboard + screen-reader users. */}
                      <button
                        type="button"
                        className={[
                          "contributor-picker__row",
                          checked ? "contributor-picker__row--selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-pressed={checked}
                        onClick={() => toggleId(c.id)}
                      >
                        <span className="contributor-picker__name">{c.name}</span>
                        <span className="contributor-picker__count">{c.count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="contributor-picker__footer">
                {selectedContributorIds.length > 0 && (
                  <button
                    type="button"
                    className="contributor-picker__link"
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                )}
                {total > TOP_LIMIT && (
                  <button
                    type="button"
                    className="contributor-picker__link contributor-picker__link--strong"
                    onClick={() => {
                      setOpen(false);
                      setModalOpen(true);
                    }}
                  >
                    More… ({total - TOP_LIMIT}+)
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {modalOpen && (
        <ContributorSearchModal
          projectId={projectId}
          selectedSet={selectedSet}
          onToggle={toggleId}
          onClose={() => setModalOpen(false)}
          onClear={clearSelection}
          selectedCount={selectedContributorIds.length}
        />
      )}
    </div>
  );
}

/**
 * Searchable modal for the full contributor catalogue.
 *
 * Debounces the search input (250ms) so typing doesn't fire a request
 * per keystroke. The same selection set is mutated as the inline
 * dropdown — closing the modal preserves the user's picks.
 */
function ContributorSearchModal({
  projectId,
  selectedSet,
  onToggle,
  onClose,
  onClear,
  selectedCount,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Esc closes; focus the search field on mount for fast keyboard use.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    if (inputRef.current) inputRef.current.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Debounced fetch. Empty query short-circuits to no results — the
  // top-20 already covers the no-search case in the inline dropdown.
  useEffect(() => {
    if (!projectId) return undefined;
    const q = query.trim();
    if (q === "") {
      setResults([]);
      setError(null);
      return undefined;
    }
    const controller = new AbortController();
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      axios
        .get(config.getApiUrl("listProjectContributors.php"), {
          params: { projectsid: projectId, search: q },
          signal: controller.signal,
        })
        .then((res) => {
          if (controller.signal.aborted) return;
          setResults(Array.isArray(res.data?.results) ? res.data.results : []);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          setError("Search failed.");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query, projectId]);

  return (
    <div
      className="contributor-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Search contributors"
      onClick={(e) => {
        // Click outside the modal body closes. Inside clicks are
        // stopped at the panel's own handler.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="contributor-modal">
        <header className="contributor-modal__header">
          <h2 className="contributor-modal__title">Find contributor</h2>
          <button
            type="button"
            className="contributor-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <input
          ref={inputRef}
          type="search"
          className="contributor-modal__search"
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />

        <div className="contributor-modal__body">
          {loading && <div className="contributor-modal__status">Searching…</div>}
          {error && (
            <div className="contributor-modal__status contributor-modal__status--error">
              {error}
            </div>
          )}
          {!loading && !error && query.trim() === "" && (
            <div className="contributor-modal__status">
              Type a name or email to search.
            </div>
          )}
          {!loading && !error && query.trim() !== "" && results.length === 0 && (
            <div className="contributor-modal__status">No matches.</div>
          )}
          {!loading && !error && results.length > 0 && (
            <ul className="contributor-modal__list">
              {results.map((c) => {
                const checked = selectedSet.has(c.id);
                return (
                  <li
                    key={c.id}
                    className="contributor-modal__item"
                    role="option"
                    aria-selected={checked}
                  >
                    {/* Item 6: toggle text rows here too, so the modal and
                        the inline dropdown share one interaction model. */}
                    <button
                      type="button"
                      className={[
                        "contributor-modal__row",
                        checked ? "contributor-modal__row--selected" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-pressed={checked}
                      onClick={() => onToggle(c.id)}
                    >
                      <span className="contributor-modal__name">{c.name}</span>
                      <span className="contributor-modal__count">{c.count}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="contributor-modal__footer">
          {selectedCount > 0 && (
            <button
              type="button"
              className="contributor-modal__link"
              onClick={onClear}
            >
              Clear all ({selectedCount})
            </button>
          )}
          <button
            type="button"
            className="contributor-modal__primary"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
